import { TFunction } from 'i18next';
import { BillingPeriodUnit } from '../types/enums/billing-period-unit';
import { MoedaVendaEnum } from '../types/enums/moeda-venda.enum';
import { formatCurrency } from './format-currency';

const DEFAULT_ACCEPT_TEXT = 'Sim, quero aproveitar a oferta';
const DEFAULT_REJECT_TEXT = 'Não, eu não quero aproveitar a oferta';
const DEFAULT_CTA_COLOR = '#077611';

type UpsellPreco = {
  valor?: number | null;
  moeda?: number | null;
  acceptText?: string | null;
  rejectText?: string | null;
  ctaColorHex?: string | null;
};

type UpsellBillingConfig = {
  billingPeriodUnit?: BillingPeriodUnit | null;
  billingPeriodCount?: number | null;
} | null;

type UpsellTrialConfig = {
  isTrialEnabled?: boolean | null;
  trialDays?: number | null;
  trialPrice?: number | null;
} | null;

type UpsellPlano = {
  id: string;
  nome: string;
  descricao?: string | null;
  valor: number;
  moeda: number;
  billingConfig?: UpsellBillingConfig;
  trialConfig?: UpsellTrialConfig;
} | null;

type UpsellSource = {
  precos?: UpsellPreco[] | null;
  planoId?: string | null;
  plano?: UpsellPlano;
  isRecurring?: boolean;
  resolvedPrice?: number | null;
  resolvedCurrency?: number | null;
  resolvedAcceptText?: string | null;
  resolvedRejectText?: string | null;
  resolvedCtaColorHex?: string | null;
  resolvedBillingConfig?: UpsellBillingConfig;
  resolvedTrialConfig?: UpsellTrialConfig;
};

export type ResolvedUpsellOffer = {
  isRecurring: boolean;
  amount: number | null;
  currency: MoedaVendaEnum | null;
  acceptText: string | null;
  rejectText: string | null;
  ctaColorHex: string | null;
  planName: string | null;
  planDescription: string | null;
  billingPeriodUnit: BillingPeriodUnit | null;
  billingPeriodCount: number | null;
  trialDays: number | null;
  trialPrice: number | null;
  hasTrial: boolean;
};

export function resolveUpsellOffer(
  source?: UpsellSource | null,
): ResolvedUpsellOffer {
  const legacyPreco = source?.precos?.[0] ?? null;
  const plano = source?.plano ?? null;
  const billingConfig =
    source?.resolvedBillingConfig ?? plano?.billingConfig ?? null;
  const trialConfig = source?.resolvedTrialConfig ?? plano?.trialConfig ?? null;

  const amount =
    source?.resolvedPrice ?? legacyPreco?.valor ?? plano?.valor ?? null;

  const currency = (source?.resolvedCurrency ??
    legacyPreco?.moeda ??
    plano?.moeda ??
    null) as MoedaVendaEnum | null;

  const acceptText =
    source?.resolvedAcceptText ??
    legacyPreco?.acceptText ??
    DEFAULT_ACCEPT_TEXT;

  const rejectText =
    source?.resolvedRejectText ??
    legacyPreco?.rejectText ??
    DEFAULT_REJECT_TEXT;

  const ctaColorHex =
    source?.resolvedCtaColorHex ??
    legacyPreco?.ctaColorHex ??
    DEFAULT_CTA_COLOR;

  return {
    isRecurring: source?.isRecurring ?? !!source?.planoId,
    amount: typeof amount === 'number' && !Number.isNaN(amount) ? amount : null,
    currency,
    acceptText,
    rejectText,
    ctaColorHex,
    planName: plano?.nome ?? null,
    planDescription: plano?.descricao ?? null,
    billingPeriodUnit: billingConfig?.billingPeriodUnit ?? null,
    billingPeriodCount: billingConfig?.billingPeriodCount ?? null,
    trialDays:
      typeof trialConfig?.trialDays === 'number' ? trialConfig.trialDays : null,
    trialPrice:
      typeof trialConfig?.trialPrice === 'number'
        ? trialConfig.trialPrice
        : null,
    hasTrial: !!trialConfig?.isTrialEnabled,
  };
}

export function getRecurringIntervalLabel(
  billingPeriodUnit: BillingPeriodUnit | null,
  billingPeriodCount: number | null,
  t: TFunction,
) {
  switch (billingPeriodUnit) {
    case BillingPeriodUnit.Month:
      return t('perMonth');
    case BillingPeriodUnit.Week:
      return t('perWeek');
    case BillingPeriodUnit.Year:
      return t('perYear');
    case BillingPeriodUnit.Day:
      return t('perDay');
    default:
      return t('cashPayment');
  }
}

export function getUpsellRecurringSummary(
  offer: ResolvedUpsellOffer,
  t: TFunction,
) {
  if (!offer.isRecurring || !offer.amount || !offer.currency) {
    return null;
  }

  const interval = getRecurringIntervalLabel(
    offer.billingPeriodUnit,
    offer.billingPeriodCount,
    t,
  );
  const recurringPrice = formatCurrency(offer.amount, offer.currency);

  if (offer.hasTrial && offer.trialDays && offer.trialDays > 0) {
    return {
      primary: t('freeForDays', { days: offer.trialDays }),
      secondary: t('thenPrice', { price: recurringPrice, interval }),
    };
  }

  if (offer.hasTrial && offer.trialPrice != null) {
    return {
      primary: t('payToday', {
        price: formatCurrency(offer.trialPrice, offer.currency),
      }),
      secondary: t('thenPrice', { price: recurringPrice, interval }),
    };
  }

  return {
    primary: t('payToday', { price: recurringPrice }),
    secondary: t('thenPrice', { price: recurringPrice, interval }),
  };
}
